import { Text, Title } from "@assetwatch/ui";
import { Fragment, type ReactNode } from "react";

function createKeyedItems(values: string[]) {
  const occurrences = new Map<string, number>();

  return values.map((value) => {
    const occurrence = occurrences.get(value) ?? 0;
    occurrences.set(value, occurrence + 1);

    return { key: `${value}-${occurrence}`, value };
  });
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);

  return createKeyedItems(segments).map(({ key, value: segment }) => {
    const boldMatch = /^\*\*([^*]+)\*\*$/.exec(segment);
    if (boldMatch) {
      return <strong key={`bold-${key}`}>{boldMatch[1]}</strong>;
    }

    const italicMatch = /^\*([^*]+)\*$/.exec(segment);
    if (italicMatch) {
      return <em key={`italic-${key}`}>{italicMatch[1]}</em>;
    }

    return <Fragment key={`text-${key}`}>{segment}</Fragment>;
  });
}

export function AskReliMarkdownContent({ content }: { content: string }) {
  const blocks = content
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-(--spacing-6)">
      {createKeyedItems(blocks).map(({ key: blockKey, value: block }) => {
        const lines = block
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        const headingMatch = /^(#{1,3})\s+(.+)$/.exec(block);
        const isNumberedList = lines.every((line) => /^\d+\.\s+/.test(line));
        const isBulletList = lines.every((line) => /^[-*]\s+/.test(line));

        if (headingMatch) {
          const level = headingMatch[1].length;
          return (
            <Title
              key={`block-${blockKey}`}
              size={level === 1 ? "md" : "sm"}
              as={level === 1 ? "h3" : "h4"}
            >
              {renderInlineMarkdown(headingMatch[2])}
            </Title>
          );
        }

        if (isNumberedList) {
          return (
            <ol
              key={`block-${blockKey}`}
              className="m-0 flex list-decimal flex-col gap-(--spacing-4) pl-(--spacing-16)"
            >
              {createKeyedItems(lines).map(({ key, value: line }) => (
                <li key={`line-${key}`}>
                  <Text size="sm" as="span">
                    {renderInlineMarkdown(line.replace(/^\d+\.\s+/, ""))}
                  </Text>
                </li>
              ))}
            </ol>
          );
        }

        if (isBulletList) {
          return (
            <ul
              key={`block-${blockKey}`}
              className="m-0 flex list-disc flex-col gap-(--spacing-4) pl-(--spacing-16)"
            >
              {createKeyedItems(lines).map(({ key, value: line }) => (
                <li key={`line-${key}`}>
                  <Text size="sm" as="span">
                    {renderInlineMarkdown(line.replace(/^[-*]\s+/, ""))}
                  </Text>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <Text key={`block-${blockKey}`} size="sm" as="div">
            {renderInlineMarkdown(block)}
          </Text>
        );
      })}
    </div>
  );
}
